// Information layers (HANDOVER Section 3, added 2026-09-19): two round
// toggles under the language toggle, each opening one bark-brown text box.
// One open at a time; tapping the active toggle closes it. A child never
// needs these; the copy comes from Fjärilshuset through the i18n layer.
import { t, getLanguage, onLanguageChange } from '../i18n/index.js';
import { el } from '../debug/controls.js';

const ICON_FACTS = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
<circle cx="12" cy="12" r="9"></circle>
<path d="M12 11v5.5M12 7.6v.4"></path>
</svg>`;
const ICON_HOW = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
<path d="M5 19c0-8 5-13 14-14 0 9-5 14-14 14z"></path>
<path d="M5 19c3-4 6-7 10-10"></path>
</svg>`;

export function createLayers(app, { speciesById }) {
  let open = null; // 'facts' | 'how' | null
  let speciesId = null; // the perched species, if any

  const title = el('h2', { class: 'infobox__title' });
  const sci = el('span', { class: 'infobox__sci' });
  const body1 = el('p', { class: 'infobox__body' });
  const body2 = el('p', { class: 'infobox__body' });
  const box = el('div', { class: 'infobox', role: 'region', 'aria-live': 'polite' }, [title, sci, body1, body2]);

  const buttons = {};
  const layersEl = el('div', { class: 'layers', role: 'group' });
  for (const [key, icon] of [
    ['facts', ICON_FACTS],
    ['how', ICON_HOW],
  ]) {
    const b = el('button', { class: 'layer', type: 'button', 'data-layer': key, 'aria-pressed': 'false' });
    b.innerHTML = icon;
    b.addEventListener('pointerdown', () => b.classList.add('is-pressed'));
    for (const type of ['pointerup', 'pointercancel', 'pointerleave']) {
      b.addEventListener(type, () => b.classList.remove('is-pressed'));
    }
    b.addEventListener('click', () => setOpen(open === key ? null : key));
    buttons[key] = b;
    layersEl.append(b);
  }

  function render() {
    const lang = getLanguage();
    buttons.facts.setAttribute('aria-label', t('layers.facts.label'));
    buttons.how.setAttribute('aria-label', t('layers.how.label'));
    for (const [key, b] of Object.entries(buttons)) b.setAttribute('aria-pressed', open === key ? 'true' : 'false');
    app.classList.toggle('has-facts', open === 'facts');
    box.classList.toggle('is-visible', open !== null);
    if (open === 'facts') {
      const s = speciesId ? speciesById[speciesId] : null;
      if (s) {
        const names = s.names[lang] ?? s.names.en;
        const facts = s.facts?.[lang] ?? s.facts?.en ?? {};
        title.textContent = names.common;
        sci.textContent = names.scientific;
        sci.hidden = false;
        body1.textContent = facts.body1 ?? '';
        body2.textContent = facts.body2 ?? '';
      } else {
        title.textContent = t('layers.facts.none.title');
        sci.hidden = true;
        body1.textContent = t('layers.facts.none.body1');
        body2.textContent = t('layers.facts.none.body2');
      }
    } else if (open === 'how') {
      title.textContent = t('layers.how.title');
      sci.hidden = true;
      body1.textContent = t('layers.how.body1');
      body2.textContent = t('layers.how.body2');
    }
    body2.hidden = !body2.textContent;
  }
  function setOpen(next) {
    open = next;
    render();
  }
  render();
  onLanguageChange(render);

  app.append(layersEl, box);
  return {
    /** Tell the facts layer which species is on the branch (null when none). */
    setSpecies(id) {
      speciesId = id;
      render();
    },
    close: () => setOpen(null),
    get open() {
      return open;
    },
  };
}
